/**
 * Resolve the language to show a visitor, following the priority order:
 *
 *   1. User profile preference (profiles.language, passed in by the app)
 *   2. Saved cookie / localStorage (set by a previous LanguageSwitcher choice)
 *   3. Browser language (navigator.language / navigator.languages)
 *   4. English fallback
 *
 * Country/IP lookup (between 3 and 4) is intentionally deferred - see the
 * i18n roadmap.
 */

import {
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  SUPPORTED_LANGUAGE_CODES,
  type LanguageCode,
} from './config';
import { readStoredLanguage } from './storage';

export interface DetectLanguageOptions {
  /** The signed-in user's saved preference, if any (profiles.language). */
  profileLang?: string | null;
}

function matchBrowserLanguage(): LanguageCode | null {
  if (typeof navigator === 'undefined') return null;
  const candidates: string[] = [];
  if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
  if (navigator.language) candidates.push(navigator.language);

  for (const raw of candidates) {
    const prefix = raw.toLowerCase().split('-')[0];
    if ((SUPPORTED_LANGUAGE_CODES as readonly string[]).includes(prefix)) {
      return prefix as LanguageCode;
    }
  }
  return null;
}

export function detectLanguage(options: DetectLanguageOptions = {}): LanguageCode {
  if (isSupportedLanguage(options.profileLang)) return options.profileLang;

  const stored = readStoredLanguage();
  if (stored) return stored;

  const browser = matchBrowserLanguage();
  if (browser) return browser;

  return DEFAULT_LANGUAGE;
}
